import React, {memo, useState} from 'react'
import cx from 'classnames'
import {useInView} from 'react-intersection-observer'

/* Components */
import TagLabel from './TagLabel'
import ProductRating from './ProductRating'
import ProductImage from './ProductImage'
import ProductTitle from './ProductTitle'
import ProductPrice from './ProductPrice'

/* Utils */
import {yieldToMain} from '../../../../utils/performance'
import DataSet from '../../../../utils/DataSet'
import {generateExperiments} from '../../../../modules/analytics/analyticsUtils'
import {generateUrlForGoogleBot} from '../../../../utils/urlHelper'
import {getBrowserCookie} from '../../../../../client/common/persistence/browserCookieUtils'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Styles */
import s from './product-card.scss'

const ProductCard = memo(({productData, contentGroupId, isBot, placementId, sponsoredWidgetTitle, sponsoredCampaignWidget, ...props}) => {
  const {campaignButtonText, link, name, campaignType, outOfStock, campaignDetail, index, merchantName, sku, productId, merchantId, listingId, priceInfo, reviewsInfo, contentId, stockInfo} = productData

  const [cardState, setCardState] = useState(
    campaignType === 'product' || campaignType === 'SponsoredProducts' ? 'defaultProduct' : 'defaultListing'
  )

  const [isPending, setIsPending] = useState(false)

  const {ref, inView} = useInView({
    threshold: 0.5,
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && sponsoredCampaignWidget) {
        let eventPayload = createSponsoredProductPayload();
        eventPayload.products[0].labels = [
          {
            type: 'incentiveToBuy',
            id: productData?.labels?.affordabilityLabels?.[0]?.tagName || null,
            displayName: productData?.labels?.affordabilityLabels?.[0]?.text || null
          },
          {
            type: 'valueAddedService',
            id: productData?.labels?.winnerLabels?.[0]?.tagName || null,
            displayName: productData?.labels?.winnerLabels?.[0]?.text || null
          },
          {
            type: 'campaign',
            id: productData?.labels?.campaignLabels?.[0]?.tagName || null,
            displayName: productData?.labels?.campaignLabels?.[0]?.text || null
          }
        ]
        window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.SPONSORED_PRODUCT_VIEW, eventPayload)
      }
    }
  })

  const isDiscounted =
    productData?.priceInfo?.onSalePrice !== productData?.priceInfo?.originalPrice

  const isProduct = (campaignType === 'product' || campaignType === 'SponsoredProducts') ?? false

  const productLabels = productData?.labels?.productLabels?.[0] ?? {}

  const handleAddToCart = async(e) => {
    e.preventDefault()
    if(sponsoredCampaignWidget) {
      e.stopPropagation()
    }
    const addToCartData = {
      listingId: productData?.listingId || '',
      sku: productData?.sku || '',
      name: productData?.name || '',
      quantity: 1
    }

    setIsPending(true)
    setCardState('Loading')
    await yieldToMain()
    handleAddToCartClickEvent()
    if (process.env.BROWSER && window?.Checkout?.AddToCart) {
      window.Checkout.AddToCart(addToCartData, 'HomePage', {
        callback: () => {
          setCardState('Added')
          setIsPending(false)
          sendAdServiceRequest()
          setTimeout(() => {
            setCardState('defaultProduct')
          }, 2000)
        },
        errorHandler: (e) => {
          setCardState('Error')
          setIsPending(false)
          setTimeout(() => {
            setCardState('defaultProduct')
          }, 2000)
          console.error('Checkout HomePage Campaign Add To Cart Error', e)
        }
      })
    }
  }

  const handleAddToCartClickEvent = () => {
    let data;
    if(!sponsoredCampaignWidget) {
      data = {
        canonical_url: window?.utagData?.canonical_url || '',
        category: {},
        category_hierarchy: '',
        category_status: '',
        experiments: generateExperiments(),
        listings: [],
        location: 'CampaignWidget',
        merchant: merchantName,
        merchantId: merchantId,
        page_type: window?.hepsiBus?.parsers?.getPageType(),
        page_value: window?.hepsiBus?.parsers?.getPageValue(),
        product: [{
          id: productId || '',
          listingId: listingId || '',
          mainProduct: true,
          merchantId: merchantId,
          name: name,
          position: index + 1,
          price: priceInfo?.originalPrice?.value?.toString(),
          quantity: 1,
          sku: sku || '',
        }],
        customerReviewCount: parseInt(reviewsInfo?.count) || '',
        customerReviewScore: reviewsInfo?.rating || ''
      }
    }else {
      data = createSponsoredProductPayload();
      data = {
        ...data,
        canonical_url: window?.utagData?.canonical_url || '',
        location: 'SponsoredCampaignWidget',
        merchant: merchantName ?? '',
        category_status: '',
      }
    }
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.ADD_TO_CART_CLICK, data, window?.hepsiBus?.parsers?.getGlobals())
  }


  const handleOpportunityClickEvent = async(e) => {
    e.stopPropagation()
    await yieldToMain()
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.LISTING_OPPORTUNITY_BUTTON, {
      contentId: contentId,
      componentType: 'CampaignCard',
      campaignType: campaignType,
      link: link,
      sku: sku,
      productId: productId,
      position: index,
      page_type: window?.hepsiBus?.parsers?.getPageType(),
      reasonValue: '',
    })
  }

  const handleClickCard = async() => {
    await yieldToMain()
    if(sponsoredCampaignWidget) {
      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.SPONSORED_PRODUCT_CLICK, createSponsoredProductPayload())
    }else {
      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.CAMPAIGN_CARD_CLICK, {
        contentId: contentId,
        componentType: 'CampaignCard',
        campaignType: campaignType,
        productId: productData?.productId,
        sku: productData?.sku,
        page_type: window?.hepsiBus?.parsers?.getPageType(),
        page_value: window?.hepsiBus?.parsers?.getPageValue(),
        link: link,
        brandName: productData?.brand?.name,
        position: index,
        reasonValue: ''
      })
    }
  }

  const cardStates = {
    defaultProduct: {
      text: 'Sepete Ekle',
      className: s.addToCart,
      onClick: handleAddToCart
    },
    defaultListing: {
      text: campaignButtonText ? campaignButtonText : 'Fırsatı Kaçırma',
      className: s.addToCart,
      onClick: handleOpportunityClickEvent
    },
    Added: {
      text: 'Sepete Eklendi',
      className: cx(s.addToCart, s.addToCartAdded)
    },
    Loading: {
      text: null,
      className: cx(s.addToCart, s.addToCartLoading)
    },
    Error: {
      text: 'Sepete Eklenemedi',
      className: cx(s.addToCart, s.addToCartError)
    }
  }

  const validateColor = (color) => {
    if(color.startsWith('#')) {
      return color
    }
    return `#${color}`
  }

  const sendAdServiceRequest = async() => {
    if (!sponsoredCampaignWidget || !productData?.link) {
      return;
    }
    const anonymousId = getBrowserCookie('hbus_anonymousId')
    const trackingApiMappingUrl = `${productData?.link}&eventKey=addtocart${anonymousId ? `&anonymousId=${anonymousId}` : ''}`
    try {
      await fetch(trackingApiMappingUrl, {
        method: 'GET',
        credentials: 'include'
      });
    } catch (error) {
      console.error('AdService request failed:', error?.message);
    }
  };

  const createSponsoredProductPayload = () => {
    return {
      page_type: window?.hepsiBus?.parsers?.getPageType(),
      page_value: window?.hepsiBus?.parsers?.getPageValue(),
      placement_id: placementId,
      placement_title: sponsoredWidgetTitle || 'Senin İçin Seçtiklerimiz',
      products: [{
        listingId: productData.listingId,
        position: props?.index + 1,
        price: productData.priceInfo?.onSalePrice?.toString(),
        product_id: productData.productId,
        sku: productData.sku,
        merchantId: productData.merchantId,
        adCampaignId: productData?.adCampaignId,
        adShowId: productData?.adShowId
      }]
    }
  }

  const isProductRatingShown = (reviewsInfo?.count > 0 && reviewsInfo?.rating > 0) || stockInfo?.count > 0
  const productImageUrl = !sponsoredCampaignWidget ? productData?.customImageUrl : productData?.sherlockImageUrls[0]?.Image
  return (
    <a
      ref={ref}
      data-hbus={
        !sponsoredCampaignWidget && DataSet.createCampaignCardViewDataForDavinci({
          pageType: 'home',
          pageValue: '',
          componentType: 'CampaignCard',
          campaignType: campaignType,
          contentId: contentId,
          contentGroupId: contentGroupId,
          productId: productData?.productId,
          sku: productData?.sku,
          position: index,
          reasonValue: ''
        })
      }
      href={!outOfStock ? generateUrlForGoogleBot({url: link, isBot}) : '#'}
      target='_blank'
      onClick={(e) => handleClickCard(e)}
      title={isProduct ? `${name} - ${productData?.priceInfo?.onSalePrice}` : `${name} - ${campaignDetail?.text}`}
      className={!outOfStock ? s.container : cx(s.container, s.containerDisabled)}>
      <ProductImage image={productImageUrl} alt={productData?.altText || ''} badgeLabel={productData?.labels?.badges?.[0]} isBot={isBot} sponsoredCampaignWidget={sponsoredCampaignWidget}/>
      <div className={s.details}>
        <div className={s.detailsTop}>
          <TagLabel tagLabels={productData?.labels?.tagLabels} outOfStock={outOfStock}/>
          <ProductTitle brand={productData?.brand?.name} name={name} isProduct={isProduct}/>
          {
            isProductRatingShown && (
              <ProductRating
                rating={productData?.reviewsInfo?.rating}
                ratingCount={productData?.reviewsInfo?.count}
                stockCount={productData?.stockInfo?.count}
                isProduct={isProduct}
                outOfStock={outOfStock}/>
            )
          }
          {
            !outOfStock && (productData?.priceInfo?.onSalePrice > 0 || productData?.priceInfo?.originalPrice > 0) &&
            <ProductPrice
              onSalePrice={productData?.priceInfo?.onSalePrice}
              originalPrice={productData?.priceInfo?.originalPrice}
              showOriginalPrice={productData?.priceInfo?.showOriginalPrice}
              isDiscounted={isDiscounted}/>
          }
          {
            priceInfo?.showPremiumPrice && (
              <div className={s.premiumPrice}>{productData?.labels?.productLabels?.text}</div>
            )
          }
          {
            (campaignDetail?.text && !outOfStock) && (
              <div className={s.campaignDetail} style={{color: validateColor(campaignDetail?.color)}}>
                {campaignDetail?.text}
              </div>
            )
          }
          {
            (productLabels?.text && !outOfStock) && (
              <div className={s.campaignDetail} style={{color: validateColor(productLabels?.textColor)}}>
                {productLabels?.text}
              </div>
            )
          }
        </div>
        <div className={s.detailsBottom}>
          <div
            className={
              cardState === 'defaultProduct' ? s.addToCart : cardStates[cardState].className
            }
            role='button'
            tabIndex='0'
            aria-live='assertive'
            aria-label={cardStates[cardState].text}
            data-test-id='cc-button'
            onClick={cardStates?.[cardState]?.onClick}>
            {!isPending && cardStates[cardState].text}
            {isPending && <div className={s.isPendingLoader}/>}
          </div>
        </div>
      </div>
    </a>
  )
})

export default ProductCard
